import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase.ts';
import type { KitchenSnapshot, KitchenOrder } from '../../lib/types.ts';
import { useKitchenRealtime } from '../../hooks/useKitchenRealtime.ts';
import { KitchenOrderCard } from '../../components/staff/KitchenOrderCard.tsx';
import { useStaff } from '../../contexts/StaffContext.tsx';
import { ORDER_STATUS_LABEL } from '../../lib/format.ts';
import type { OrderStatus } from '../../lib/types.ts';

const ACTIVE: OrderStatus[] = ['new', 'accepted', 'cooking', 'ready'];

async function fetchKitchenOrders(tenantSlug: string, locationLocalId: string): Promise<KitchenSnapshot> {
  const { data, error } = await supabase.rpc('get_kitchen_orders', {
    p_tenant_slug: tenantSlug,
    p_location_local_id: locationLocalId,
  });
  if (error) throw error;
  return data as KitchenSnapshot;
}

export function KitchenPage() {
  const { activeMembership } = useStaff();
  const [filter, setFilter] = useState<OrderStatus | 'all'>('all');

  const tenantSlug = activeMembership?.tenant_slug ?? '';
  const locationLocalId = activeMembership?.locations?.[0]?.local_id ?? '';

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['kitchen-orders', tenantSlug, locationLocalId],
    queryFn: () => fetchKitchenOrders(tenantSlug, locationLocalId),
    enabled: !!tenantSlug && !!locationLocalId,
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  useKitchenRealtime({
    tenantSlug,
    locationLocalId,
    enabled: !!tenantSlug && !!locationLocalId,
  });

  if (!activeMembership) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6 text-center" style={{ background: 'var(--bg)' }}>
        <p style={{ color: 'var(--text-muted)' }}>Нет доступных заведений</p>
      </div>
    );
  }

  if (isLoading) return <KitchenSkeleton />;

  if (isError || !data) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center" style={{ background: 'var(--bg)' }}>
        <p className="text-base font-semibold" style={{ color: 'var(--text)' }}>
          Не удалось загрузить заказы
        </p>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {(error as Error)?.message}
        </p>
        <button
          onClick={() => refetch()}
          className="rounded-lg px-5 py-2.5 text-sm font-medium"
          style={{ background: 'var(--accent)', color: 'var(--accent-fg)' }}
        >
          Повторить
        </button>
      </div>
    );
  }

  const orders: KitchenOrder[] = data.orders ?? [];
  const visible = filter === 'all' ? orders : orders.filter((o) => o.status === filter);
  const counts = ACTIVE.reduce<Record<string, number>>((acc, s) => {
    acc[s] = orders.filter((o) => o.status === s).length;
    return acc;
  }, {});

  return (
    <div className="flex min-h-dvh flex-col" style={{ background: 'var(--bg)' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-20 px-4 py-3"
        style={{ background: 'var(--surface)', boxShadow: 'var(--shadow-sm)' }}
      >
        <div className="flex items-center justify-between gap-2">
          <div>
            <h1 className="text-base font-semibold" style={{ color: 'var(--text)' }}>
              Кухня — {activeMembership.tenant_name}
            </h1>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {activeMembership.locations?.[0]?.name ?? ''} · {data.role}
            </p>
          </div>
          <span
            className="text-xs px-2 py-1 rounded-full font-medium"
            style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
          >
            {orders.length} активных
          </span>
        </div>

        {/* Status filter tabs */}
        <div className="mt-2 flex gap-1.5 overflow-x-auto scrollbar-none">
          <FilterPill
            label="Все"
            count={orders.length}
            active={filter === 'all'}
            onClick={() => setFilter('all')}
          />
          {ACTIVE.map((s) => (
            <FilterPill
              key={s}
              label={ORDER_STATUS_LABEL[s]}
              count={counts[s]}
              active={filter === s}
              onClick={() => setFilter(s)}
            />
          ))}
        </div>
      </header>

      <main className="flex-1 p-3">
        {visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 pt-20 text-center">
            <p className="text-4xl" aria-hidden="true">🎉</p>
            <p className="text-base font-medium" style={{ color: 'var(--text)' }}>
              {filter === 'all' ? 'Нет активных заказов' : `Нет заказов со статусом «${ORDER_STATUS_LABEL[filter]}»`}
            </p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((order) => (
              <KitchenOrderCard
                key={order.order_id}
                order={order}
                currency="RUB"
                tenantSlug={tenantSlug}
                locationLocalId={locationLocalId}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function FilterPill({
  label, count, active, onClick,
}: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex-shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors"
      style={{
        background: active ? 'var(--accent)' : 'var(--n-100)',
        color: active ? '#fff' : 'var(--text-muted)',
        minHeight: 32,
      }}
    >
      {label}
      {count > 0 && (
        <span
          className="ml-1.5 rounded-full px-1.5 py-0.5 text-xs font-bold"
          style={{
            background: active ? 'rgba(255,255,255,0.25)' : 'var(--border)',
            color: active ? '#fff' : 'var(--text)',
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function KitchenSkeleton() {
  return (
    <div className="flex min-h-dvh flex-col" style={{ background: 'var(--bg)' }}>
      <div className="h-20 animate-pulse" style={{ background: 'var(--surface)' }} />
      <div className="grid gap-3 p-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-48 animate-pulse rounded-xl" style={{ background: 'var(--surface)' }} />
        ))}
      </div>
    </div>
  );
}
